import React, { useState, useRef, useEffect } from 'react';
import { chatApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Robot, X, PaperPlaneTilt, Sparkle, User, ArrowsOut
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function ChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const response = await chatApi.send(userMsg, sessionId);
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
          data-testid="chat-bubble-btn"
        >
          <Robot size={26} weight="fill" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[520px] bg-card border border-border rounded-sm shadow-2xl flex flex-col animate-fade-in" data-testid="chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center">
                <Sparkle size={18} className="text-primary-foreground" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm">BIL AI</h3>
                <p className="text-xs text-muted-foreground">Business Intelligence Assistant</p>
              </div>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigate('/ai-assistant')} title="Open full page">
                <ArrowsOut size={16} />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setIsOpen(false)} data-testid="close-chat">
                <X size={16} />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="text-center pt-12">
                <Robot size={36} className="mx-auto text-muted-foreground mb-3 opacity-40" />
                <p className="text-sm font-medium">How can I help?</p>
                <p className="text-xs text-muted-foreground mt-1">Ask about deals, contacts, revenue, or business strategy</p>
                <div className="mt-4 space-y-2">
                  {['Show me top deals', 'Revenue forecast', 'How to grow pipeline?'].map((q) => (
                    <button
                      key={q}
                      className="block w-full text-left text-xs p-2 border border-border rounded-sm hover:bg-muted transition-colors"
                      onClick={() => { setInput(q); }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="h-7 w-7 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkle size={14} className="text-primary" />
                  </div>
                )}
                <div className={`max-w-[80%] p-3 rounded-sm text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="h-7 w-7 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                    <User size={14} />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="h-7 w-7 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkle size={14} className="text-primary" />
                </div>
                <div className="bg-muted p-3 rounded-sm">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border">
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask BIL AI..."
                className="flex-1 rounded-sm text-sm"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button type="submit" size="sm" disabled={loading || !input.trim()} className="rounded-sm" data-testid="chat-send">
                <PaperPlaneTilt size={18} />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
